import { createContext, useState, FC, ReactNode, useEffect } from 'react';
import { InterestProviderItem, InterestProvidersListFiltersFormFields } from '../../../../@types';
import { IntlProvider } from 'react-intl';
import { getQueryParam } from '../../../../utils';
import en from '../translations/en';
import nl from '../translations/nl';

export interface InterestProvidersContextType {
  interestProviders: InterestProviderItem[];
  filters: InterestProvidersListFiltersFormFields | null;
  hideList: boolean;
  isLoading: boolean;
  setInterestProviders: (items: InterestProviderItem[]) => void;
  setFilters: (filters: InterestProvidersListFiltersFormFields) => void;
  setIsLoading: (isLoading: boolean) => void;
  setHideList: (hideList: boolean) => void;
}

export const InterestProvidersContext = createContext<InterestProvidersContextType>({
  interestProviders: [],
  filters: null,
  hideList: false,
  isLoading: true,
  setInterestProviders: () => {
    throw new Error('setInterestProviders function must be overridden');
  },
  setFilters: () => {
    throw new Error('setFilters function must be overridden');
  },
  setIsLoading: () => {
    throw new Error('setIsLoading function must be overridden');
  },
  setHideList: () => {
    throw new Error('setHideList function must be overridden');
  }
});

export const InterestProvidersProvider: FC<{ children: ReactNode }> = ({ children }) => {
  // from path url or locale param
  let lang = 'nl';
  if (window.location.pathname.includes('/en/')) {
    lang = 'en';
  }
  const paramLang = getQueryParam('lang');
  if (paramLang) {
    lang = paramLang;
  }
  const [locale] = useState(lang);
  const [messages, setMessages] = useState({});
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);
  const [hideList, setHideList] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [interestProviders, setInterestProviders] = useState<InterestProviderItem[]>([]);
  const [filters, setFilters] = useState<InterestProvidersListFiltersFormFields | null>(null);

  useEffect(() => {
    setIsLoadingMessages(true); // Set loading to true when locale changes
    setMessages(lang === 'en' ? en : nl);
    setIsLoadingMessages(false); // Set loading to false when messages are set
  }, [locale]);

  if (isLoadingMessages) {
    return <div>Loading...</div>; // Show a loading message or a spinner
  }

  return (
    <IntlProvider locale={locale} messages={messages}>
      <InterestProvidersContext.Provider
        value={{
          interestProviders,
          filters,
          isLoading,
          hideList,
          setInterestProviders,
          setFilters,
          setIsLoading,
          setHideList
        }}>
        {children}
      </InterestProvidersContext.Provider>
    </IntlProvider>
  );
};
